import { useState, useEffect, useCallback, useRef } from 'react';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Expense, ExpenseFormData, ExpenseFilters } from '../types';


interface UseExpensesReturn {
  expenses: Expense[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  canAddMore: boolean;
  freeExpensesLeft: number;
  createExpense: (data: ExpenseFormData) => Promise<Expense>;
  updateExpense: (id: string, data: Partial<ExpenseFormData>) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
}

const DEFAULT_FILTERS: ExpenseFilters = {
  search: '',
  category: 'all',
  dateFrom: '',
  dateTo: '',
  recurring: 'all',
};

// Returns true if expense passes all active filters (for realtime INSERT matching)
function matchesFilters(exp: Expense, filters: ExpenseFilters): boolean {
  if (exp.is_template) return false;  // templates never appear in expense list
  if (filters.search && !exp.description.toLowerCase().includes(filters.search.toLowerCase())) return false;
  if (filters.category !== 'all' && exp.category !== filters.category) return false;
  if (filters.dateFrom && exp.date < filters.dateFrom) return false;
  if (filters.dateTo && exp.date > filters.dateTo) return false;
  if (filters.recurring !== 'all' && String(exp.recurring) !== filters.recurring) return false;
  return true;
}

export function useExpenses(filters: ExpenseFilters = DEFAULT_FILTERS): UseExpensesReturn {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const optimisticCreatedRef = useRef<Set<string>>(new Set());
  const optimisticDeletedRef = useRef<Set<string>>(new Set());

  // Keep a stable ref to totalCount so realtime callbacks can read latest value
  const totalCountRef = useRef(totalCount);
  useEffect(() => { totalCountRef.current = totalCount; }, [totalCount]);

  const fetchExpenses = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('expenses')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .eq('is_template', false)          // templates appear in UpcomingRecurring, not here
        .order('date', { ascending: false })
        .order('created_at', { ascending: false });

      if (filters.search)               query = query.ilike('description', `%${filters.search}%`);
      if (filters.category !== 'all')   query = query.eq('category', filters.category);
      if (filters.dateFrom)             query = query.gte('date', filters.dateFrom);
      if (filters.dateTo)               query = query.lte('date', filters.dateTo);
      if (filters.recurring !== 'all')  query = query.eq('recurring', filters.recurring === 'true');

      const { data, error: fetchError, count } = await query;
      if (fetchError) throw fetchError;

      setExpenses((data as Expense[]) ?? []);
      setTotalCount(count ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  }, [user, filters.search, filters.category, filters.dateFrom, filters.dateTo, filters.recurring]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // ── Realtime: apply per-event patches instead of full refetch ──────────────
  useEffect(() => {
    if (!user) return;

    // Unique channel name per user to avoid cross-instance conflicts
    const channelName = `expenses:${user.id}`;

    const channel = supabase
      .channel(channelName)
      .on<Expense>(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'expenses', filter: `user_id=eq.${user.id}` },
        (payload: RealtimePostgresChangesPayload<Expense>) => {
          const row = payload.new as Expense;
          if (optimisticCreatedRef.current.has(row.id)) {
            optimisticCreatedRef.current.delete(row.id);
            return;
          }
          let inserted = false;
          if (matchesFilters(row, filters)) {
            setExpenses(prev => {
              if (prev.some(e => e.id === row.id)) return prev;
              inserted = true;
              // Insert in correct date order (newest first)
              const idx = prev.findIndex(e => e.date < row.date || (e.date === row.date && e.created_at < row.created_at));
              const next = [...prev];
              next.splice(idx === -1 ? next.length : idx, 0, row);
              return next;
            });
            if (inserted) setTotalCount(c => c + 1);
          } else {
            // Item exists but is filtered out in current view: still count globally
            setTotalCount(c => c + 1);
          }
        },
      )
      .on<Expense>(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'expenses', filter: `user_id=eq.${user.id}` },
        (payload: RealtimePostgresChangesPayload<Expense>) => {
          const row = payload.new as Expense;
          if (matchesFilters(row, filters)) {
            setExpenses(prev => prev.map(e => (e.id === row.id ? row : e)));
          } else {
            // Row no longer matches active filters → remove from visible list
            setExpenses(prev => prev.filter(e => e.id !== row.id));
          }
        },
      )
      .on<Expense>(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'expenses', filter: `user_id=eq.${user.id}` },
        (payload: RealtimePostgresChangesPayload<Expense>) => {
          const oldRow = payload.old as Pick<Expense, 'id'>;
          if (optimisticDeletedRef.current.has(oldRow.id)) {
            optimisticDeletedRef.current.delete(oldRow.id);
            return;
          }
          let removed = false;
          setExpenses(prev => {
            if (!prev.some(e => e.id === oldRow.id)) return prev;
            removed = true;
            return prev.filter(e => e.id !== oldRow.id);
          });
          // If row was not visible (filtered out), still decrement global count
          if (removed) setTotalCount(c => Math.max(0, c - 1));
          else setTotalCount(c => Math.max(0, c - 1));
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, filters]);   // re-subscribe when filters change so matchesFilters uses updated closure

  const canAddMore = true;
  const freeExpensesLeft = Infinity;

  // ── CRUD ───────────────────────────────────────────────────────────────────

  const createExpense = async (data: ExpenseFormData): Promise<Expense> => {
    if (!user) throw new Error('Not authenticated');
    // Derive is_template from recurring_type: recurring templates don't appear in expense log
    const isTemplate = data.recurring_type !== 'none';

    const { data: created, error } = await supabase
      .from('expenses')
      .insert({
        ...data,
        user_id: user.id,
        is_template: isTemplate,
        // Log-dated expenses (non-templates) increment the limit count
      })
      .select()
      .single();

    if (error) throw error;

    const createdExpense = created as Expense;
    optimisticCreatedRef.current.add(createdExpense.id);
    setTotalCount(c => c + 1);
    if (matchesFilters(createdExpense, filters)) {
      setExpenses(prev => {
        if (prev.some(e => e.id === createdExpense.id)) return prev;
        const idx = prev.findIndex(
          e => e.date < createdExpense.date || (e.date === createdExpense.date && e.created_at < createdExpense.created_at),
        );
        const next = [...prev];
        next.splice(idx === -1 ? next.length : idx, 0, createdExpense);
        return next;
      });
    }

    return createdExpense;
  };

  const updateExpense = async (id: string, data: Partial<ExpenseFormData>): Promise<void> => {
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('expenses')
      .update(data)
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;

    setExpenses(prev => prev
      .map(e => (e.id === id ? { ...e, ...data } : e))
      .filter(e => matchesFilters(e, filters)));
  };

  const deleteExpense = async (id: string): Promise<void> => {
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;

    let removed = false;
    optimisticDeletedRef.current.add(id);
    setExpenses(prev => {
      if (!prev.some(e => e.id === id)) return prev;
      removed = true;
      return prev.filter(e => e.id !== id);
    });
    if (removed) setTotalCount(c => Math.max(0, c - 1));
  };

  return {
    expenses,
    loading,
    error,
    totalCount,
    canAddMore,
    freeExpensesLeft,
    createExpense,
    updateExpense,
    deleteExpense,
    refetch: fetchExpenses,
  };
}
